"use client";
import Link from "next/link";
import { useSearchParams, useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { usePathname } from "next/navigation";
import { cleanUrl } from "@packages/lib/utlils/url-builder";
const Paginations = ({ totalPages, initialPage, title }: any) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathName = usePathname();
  // Ensure totalPages and initialPage are valid numbers
  const validTotalPages =
    Number(totalPages) > 0 ? Math.ceil(Number(totalPages)) : 1;
  const validInitialPage = Number(initialPage) > 0 ? Number(initialPage) : 1;
  //console.log
  const [currentPage, setCurrentPage] = useState(validInitialPage);

  // Sync currentPage with URL query parameter 'pageno'
  useEffect(() => {
    const pageFromQuery =
      Number(searchParams.get("pageno")) ||
      Number(searchParams.get("page_no")) ||
      1;
    // Ensure currentPage doesn’t exceed totalPages or go below 1
    setCurrentPage(Math.min(Math.max(1, pageFromQuery), validTotalPages));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, validTotalPages]);

  // Function to build URL with existing query params and updated pageno
  const buildUrl = (page: number | string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (typeof page === "number") {
      const key = process.env.PROJECT === "Whatuni" ? "pageno" : "page_no";
      if (Number(page) === 1) {
        params.delete(key); // Remove param for page 1
      } else {
        params.set(key, page.toString());
      }
    }
    // Note: the "title" param is intentionally not added to keep pagination
    // URLs clean (e.g. /advice/career-advice?pageno=3). The article grid
    // treats the pageno as applying to its section when title is absent.
    return cleanUrl(`${pathName}?${decodeURIComponent(params.toString())}`);
  };

  const pageNumbers: (number | string)[] = [];
  const totalVisiblePages = 7;

  if (validTotalPages <= totalVisiblePages) {
    for (let i = 1; i <= validTotalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1); // Always show first page
    if (currentPage <= 3) {
      for (let i = 2; i <= 5; i++) {
        pageNumbers.push(i);
      }
      pageNumbers.push("...");
    } else if (currentPage >= validTotalPages - 2) {
      pageNumbers.push("...");
      for (let i = validTotalPages - 4; i < validTotalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push("...");
      pageNumbers.push(currentPage - 1);
      pageNumbers.push(currentPage);
      pageNumbers.push(currentPage + 1);
      pageNumbers.push("...");
    }
    pageNumbers.push(validTotalPages); // Always show last page
  }

  const items = pageNumbers;

  // Calculate nextPage safely
  const nextPage = Math.min(currentPage + 1, validTotalPages);

  const handlePagination: any = (url: string) => {
    //SR
    // const resultSkeleton = window?.document?.getElementById(
    //   "resultSectionSkeleton"
    // );
    // resultSkeleton?.classList?.remove("hidden");
    emitter.emit("searchresultloading", true);
    const srpageBodySection =
      window?.document?.getElementById("srpageBodySection");
    //PR
    // const PrPageBodySectionSkeleton = window?.document?.getElementById(
    //   "PrPageBodySectionSkeleton"
    // );
    emitter.emit("providerresultloading", true);
    const prPageBodySection =
      window?.document?.getElementById("prPageBodySection");
    window?.history?.pushState(null, "", url);

    srpageBodySection?.classList?.add("hidden");
    // Show skeleton loader for open days pagination
    const srpageBodySectionSkeleton = window?.document?.getElementById(
      "srpageBodySectionSkeleton",
    );
    srpageBodySectionSkeleton?.classList?.remove("hidden");
    prPageBodySection?.classList?.add("hidden");
    // PrPageBodySectionSkeleton?.classList?.remove("hidden");
    router.push(url);
  };

  // Intercept left-clicks so we keep the client-side navigation behaviour
  // (skeleton loading, smooth scroll) while the anchor still exposes a real,
  // crawlable href for search engines and middle/ctrl-click.
  const onPaginationLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      return; // allow default browser behaviour (new tab, etc.)
    }
    e.preventDefault();
    handlePagination(url);
  };

  return (
    <>
      {validTotalPages > 1 && (
        <div className="py-[24px] md:py-[40px]">
          <nav aria-label="navigation">
            <ul className="pagination flex justify-center items-center gap-[8px]">
              {currentPage > 1 && (
                <li>
                  <Link
                    href={buildUrl(currentPage - 1)}
                    aria-label="Previous page"
                    rel="prev"
                    onClick={(e) =>
                      onPaginationLinkClick(e, buildUrl(currentPage - 1))
                    }
                    className="hover:bg-blue-100 cursor-pointer flex items-center justify-center text-center px-[2px] py-[8px] rounded-[4px] w-[36px] h-[36px]"
                  >
                    <svg
                      width="7"
                      height="12"
                      viewBox="0 0 7 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 11L1 6L6 1"
                        stroke="#333F48"
                        strokeWidth="1.67"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </li>
              )}
              {items.map((item, index) => (
                <li key={index}>
                  {item === "..." ? (
                    <span
                      aria-hidden="true"
                      className="block small w-[36px] h-[36px] font-normal text-grey300 text-center px-[2px] py-[8px] rounded-[4px]"
                    >
                      {item}
                    </span>
                  ) : (
                    <Link
                      href={buildUrl(item)}
                      aria-label={`Go to page ${item}`}
                      aria-current={currentPage === item ? "page" : undefined}
                      onClick={(e) => onPaginationLinkClick(e, buildUrl(item))}
                      className={
                        currentPage === item
                          ? "block small w-[36px] h-[36px] font-normal text-center px-[2px] py-[8px] rounded-[4px] bg-primary-400 text-white"
                          : "block small w-[36px] h-[36px] font-normal text-grey300 text-center px-[2px] py-[8px] rounded-[4px] cursor-pointer hover:bg-blue-100 hover:text-primary-400"
                      }
                    >
                      {item}
                    </Link>
                  )}
                </li>
              ))}
              {currentPage < validTotalPages && (
                <li>
                  <Link
                    href={buildUrl(nextPage)}
                    aria-label="Next page"
                    rel="next"
                    onClick={(e) =>
                      onPaginationLinkClick(e, buildUrl(nextPage))
                    }
                    className="hover:bg-blue-100 cursor-pointer flex items-center justify-center text-center px-[2px] py-[8px] rounded-[4px] w-[36px] h-[36px]"
                  >
                    <svg
                      width="7"
                      height="12"
                      viewBox="0 0 7 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L6 6L1 11"
                        stroke="#333F48"
                        strokeWidth="1.67"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}
    </>
  );
};

export default Paginations;
